import React, { useEffect, useState, useMemo } from "react";
import "./table.scss";
import ascAec from "../../assets/images/asc-dec.svg";
import Pagination from "../Pagination";

const CommonTable = ({
  columns,
  rows = [],
  setHasData = () => {},
  page,
  totalPages,
  onPageChange,
  onRowClick,
  pageSize = 5, // default 5 rows per page (client-side)
  onSortChange, // new optional prop for API-side sorting
}) => {
  const [sortConfig, setSortConfig] = useState({ field: null, order: "asc" });
  const [localPage, setLocalPage] = useState(1);

  // Sort handler
  const handleSort = (field) => {
    let order = "asc";
    if (sortConfig.field === field && sortConfig.order === "asc") {
      order = "desc";
    }
    const newConfig = { field, order };
    setSortConfig(newConfig);

    if (onSortChange) {
      // API will handle sorting
      onSortChange(newConfig);
    }
  };

  useEffect(() => {
    setHasData(rows.length > 0);
  }, [rows, setHasData]);

  // Apply sorting (only if API-side sorting is NOT used)
  const sortedRows = useMemo(() => {
    if (onSortChange) {
      return rows; // API already returns sorted data
    }
    if (!sortConfig.field) return rows;
    return [...rows].sort((a, b) => {
      const aVal = a[sortConfig.field];
      const bVal = b[sortConfig.field];

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      return sortConfig.order === "asc"
        ? aVal.toString().localeCompare(bVal.toString())
        : bVal.toString().localeCompare(aVal.toString());
    });
  }, [rows, sortConfig, onSortChange]);

  // Client-side pagination fallback
  const effectivePage = page !== undefined ? page : localPage;
  const effectiveTotalPages =
    totalPages !== undefined ? totalPages : Math.ceil(sortedRows.length / pageSize);

  const paginatedRows =
    totalPages !== undefined
      ? sortedRows // server handles paging
      : sortedRows.slice((effectivePage - 1) * pageSize, effectivePage * pageSize);

  const handlePageChange = (newPage) => {
    if (onPageChange) {
      onPageChange(newPage); // server-side
    } else {
      setLocalPage(newPage); // client-side
    }
  };

  return (
    <>
      <div className="table-container">
        <table className="custom-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.field}>
                  <div className="flex-head">
                    {col.headerName}
                    {col.sortable && (
                      <button
                        type="button"
                        onClick={() => handleSort(col.field)}
                        aria-label={`${col.headerName} ${sortConfig.field === col.field  ? sortConfig.order === "asc" ? `Sorted ascending`  : `Sorted descending` : `Not sorted`}`}
                        className={`sort-icon ${
                          sortConfig.field === col.field ? sortConfig.order : "asc"
                        }`}
                      >
                        <img
                          src={ascAec}
                          alt=""
                          aria-hidden="true"
                          className={`sort-icon ${
                            sortConfig.field === col.field ? sortConfig.order : "desc"
                          }`}
                        />
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedRows.length > 0 ? (
              paginatedRows.map((row, idx) => (
                <tr 
                  key={row.id || idx} 
                  onClick={(e) => {
                    if (onRowClick) {
                      onRowClick(row, idx, e);
                    }
                  }}
                  style={{ cursor: 'pointer'}}>
                  {columns.map((col) => (
                    <td key={col.field}>
                      {typeof col.renderCell === "function"
                        ? col.renderCell({ row })
                        : row[col.field]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="no-data"
                  role="status"
                  aria-live="polite"
                  data-testid="no-data-available"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {effectiveTotalPages > 1 && (
        <Pagination
          page={effectivePage}
          totalPages={effectiveTotalPages}
          onPageChange={handlePageChange}
        />
      )}
    </>
  );
};

export default CommonTable;
